import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { orderService } from '../services/orderService';
import { uploadService } from '../services/uploadService';
import { scraperService } from '../services/scraperService';
import Navbar from './Navbar';
import Footer from './Footer';

const OrderForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  
  // Plateforme commune à tous les produits
  const [platform, setPlatform] = useState(location.state?.platform || 'Shein');
  
  // Tableau de produits
  const [items, setItems] = useState([
    {
      imageUrl: '',
      productLink: '',
      size: '',
      color: '',
      quantity: 1,
      comment: '',
      imageFile: null,
      imagePreview: null,
      scraping: false,
      scrapedInfo: null,
    }
  ]);

  const [uploadingImages, setUploadingImages] = useState({});
  const [generalComment, setGeneralComment] = useState('');

  // Mettre à jour la plateforme si elle vient de la navigation
  useEffect(() => {
    if (location.state?.platform) {
      setPlatform(location.state.platform);
    }
  }, [location.state]);

  const handlePlatformChange = (e) => {
    setPlatform(e.target.value);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      [field]: value,
    };
    setItems(newItems);
  };

  const handleImageChange = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.warning('L\'image est trop grande. Taille maximale: 5MB');
        return;
      }

      const newItems = [...items];
      newItems[index].imageFile = file;
      
      const reader = new FileReader();
      reader.onloadend = () => {
        newItems[index].imagePreview = reader.result;
        setItems(newItems);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleScrapeProduct = async (index) => {
    const item = items[index];
    if (!item.productLink || !platform) {
      toast.warning('Veuillez remplir le lien du produit et sélectionner une plateforme');
      return;
    }

    try {
      new URL(item.productLink);
    } catch (e) {
      toast.warning('Veuillez entrer une URL valide (commençant par http:// ou https://)');
      return;
    }

    const newItems = [...items];
    newItems[index].scraping = true;
    newItems[index].scrapedInfo = null;
    setItems(newItems);

    try {
      const result = await scraperService.verifyProduct(item.productLink, platform);
      
      if (result.success && result.productInfo) {
        newItems[index].scrapedInfo = result.productInfo;
        console.log('✅ Informations récupérées:', result.productInfo);
      } else {
        console.warn('Le scraping a échoué, mais vous pouvez continuer');
      }
    } catch (error) {
      console.error('Erreur lors du scraping:', error);
      console.warn('Le scraping a échoué, mais vous pouvez continuer à créer la commande');
    } finally {
      newItems[index].scraping = false;
      setItems(newItems);
    }
  };

  const addProduct = () => {
    setItems([
      ...items,
      {
        imageUrl: '',
        productLink: '',
        size: '',
        color: '',
        quantity: 1,
        comment: '',
        imageFile: null,
        imagePreview: null,
        scraping: false,
        scrapedInfo: null,
      }
    ]);
  };

  const removeProduct = (index) => {
    if (items.length > 1) {
      const newItems = items.filter((_, i) => i !== index);
      setItems(newItems);
    } else {
      toast.warning('Vous devez avoir au moins un produit dans votre commande');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userId = user?.id || localStorage.getItem('userId');
      
      if (!userId) {
        toast.warning('Vous devez être connecté pour créer une commande');
        navigate('/login');
        return;
      }

      // Validation
      if (!platform) {
        toast.warning('Veuillez sélectionner une plateforme');
        setLoading(false);
        return;
      }

      if (items.length === 0) {
        toast.warning('Veuillez ajouter au moins un produit');
        setLoading(false);
        return;
      }

      // Vérifier que tous les produits ont un lien
      for (let i = 0; i < items.length; i++) {
        if (!items[i].productLink) {
          toast.warning(`Le produit ${i + 1} doit avoir un lien`);
          setLoading(false);
          return;
        }
      }

      // Uploader les images
      const itemsWithImages = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        let imageUrl = item.imageUrl;

        if (item.imageFile) {
          setUploadingImages(prev => ({ ...prev, [i]: true }));
          try {
            const uploadResult = await uploadService.uploadImage(item.imageFile);
            
            if (uploadResult.success && uploadResult.imageUrl) {
              imageUrl = /^https?:\/\//i.test(uploadResult.imageUrl)
                ? uploadResult.imageUrl
                : `${(process.env.REACT_APP_API_URL || '').replace(/\/api$/, '')}${uploadResult.imageUrl}`;
            } else {
              console.warn(`Erreur lors de l'upload de l'image du produit ${i + 1}: ${uploadResult.error}`);
            }
          } catch (error) {
            console.warn(`Erreur lors de l'upload de l'image du produit ${i + 1}:`, error);
          } finally {
            setUploadingImages(prev => ({ ...prev, [i]: false }));
          }
        }

        itemsWithImages.push({
          imageUrl: imageUrl || '',
          productLink: item.productLink,
          size: item.size || '',
          color: item.color || '',
          quantity: parseInt(item.quantity, 10) || 1,
          comment: item.comment || '',
          verifiedProductName: item.scrapedInfo?.name || '',
          verifiedPrice: item.scrapedInfo?.price || 0,
          verifiedAvailability: item.scrapedInfo?.availability || false,
        });
      }

      const orderData = {
        userId,
        platform,
        items: itemsWithImages,
        comment: generalComment,
        skipScraping: true,
      };

      const response = await orderService.createOrder(orderData);

      if (response.success) {
        toast.success(`Commande créée avec succès ! ${items.length} produit(s) ajouté(s).`);
        navigate('/dashboard');
      } else {
        toast.error(`Erreur lors de la création de la commande: ${response.error}`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Une erreur est survenue');
    } finally {
      setLoading(false);
      setUploadingImages({});
    }
  };

  return (
    <div>
      <Navbar />

      <div className="container">
        <div className="card" style={{ maxWidth: '1000px', margin: '40px auto' }}>
          <h1 style={{ marginBottom: '30px', color: '#FF0000', fontSize: '32px', fontWeight: '700' }}>
            📦 Nouvelle Commande
          </h1>

          <div style={{ marginBottom: '30px', padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
            <p style={{ margin: 0, color: '#0369a1', fontSize: '14px' }}>
              💡 <strong>Astuce :</strong> Vous pouvez ajouter plusieurs produits de la même plateforme dans une seule commande. Cela vous permettra de regrouper vos achats et de faciliter le suivi.
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Plateforme commune */}
            <div className="form-group">
              <label htmlFor="platform">🌐 Plateforme * (commune à tous les produits)</label>
              <select
                id="platform"
                name="platform"
                value={platform}
                onChange={handlePlatformChange}
                required
              >
                <option value="Shein">Shein</option>
                <option value="Amazon">Amazon</option>
                <option value="Alibaba">Alibaba</option>
                <option value="AliExpress">AliExpress</option>
                <option value="Autre">Autre</option>
              </select>
            </div>

            {/* Liste des produits */}
            <div style={{ marginBottom: '30px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0, color: '#333', fontSize: '24px' }}>
                  Produits ({items.length})
                </h2>
                <button
                  type="button"
                  onClick={addProduct}
                  className="btn btn-secondary"
                  style={{ padding: '8px 16px', fontSize: '14px' }}
                >
                  + Ajouter un produit
                </button>
              </div>

              {items.map((item, index) => (
                <div key={index} className="card" style={{ marginBottom: '20px', padding: '20px', border: '2px solid #e8e8e8' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h3 style={{ margin: 0, color: '#FF0000', fontSize: '18px' }}>
                      Produit {index + 1}
                    </h3>
                    {items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeProduct(index)}
                        className="btn btn-secondary"
                        style={{ padding: '6px 12px', fontSize: '12px', backgroundColor: '#dc3545', color: 'white', border: 'none' }}
                      >
                        ✕ Supprimer
                      </button>
                    )}
                  </div>

                  {/* Upload d'image */}
                  <div className="form-group">
                    <label>📸 Image du produit (optionnel)</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageChange(index, e)}
                      disabled={uploadingImages[index] || loading}
                    />
                    {uploadingImages[index] && (
                      <p style={{ color: '#666', fontSize: '14px', marginTop: '5px' }}>
                        ⏳ Upload en cours...
                      </p>
                    )}
                    {item.imagePreview && !uploadingImages[index] && (
                      <div style={{ marginTop: '10px' }}>
                        <img
                          src={item.imagePreview}
                          alt="Aperçu"
                          style={{
                            maxWidth: '150px',
                            maxHeight: '150px',
                            borderRadius: '8px',
                            border: '1px solid #ddd',
                            objectFit: 'cover',
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Lien du produit */}
                  <div className="form-group">
                    <label>🔗 Lien du produit *</label>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                      <input
                        type="url"
                        value={item.productLink}
                        onChange={(e) => handleItemChange(index, 'productLink', e.target.value)}
                        placeholder="https://..."
                        required
                        style={{ flex: 1 }}
                        disabled={item.scraping}
                      />
                      <button
                        type="button"
                        onClick={() => handleScrapeProduct(index)}
                        disabled={!item.productLink || !platform || item.scraping || loading}
                        className="btn btn-secondary"
                        style={{ whiteSpace: 'nowrap', minWidth: '120px' }}
                      >
                        {item.scraping ? '⏳ Analyse...' : '🔍 Vérifier'}
                      </button>
                    </div>
                    {item.scraping && (
                      <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
                        <p style={{ color: '#856404', fontSize: '14px', margin: 0 }}>
                          ⏳ Récupération des informations en cours...
                        </p>
                      </div>
                    )}
                    {item.scrapedInfo && !item.scraping && (
                      <div style={{ 
                        marginTop: '10px', 
                        padding: '10px', 
                        backgroundColor: '#f0f9ff', 
                        borderRadius: '8px',
                        border: '1px solid #bae6fd'
                      }}>
                        <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', color: '#0369a1' }}>
                          ✅ Informations récupérées :
                        </p>
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Nom :</strong> {item.scrapedInfo.name || 'Non disponible'}
                        </p>
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Prix :</strong> {item.scrapedInfo.price ? `$${item.scrapedInfo.price.toFixed(2)}` : 'Non disponible'}
                        </p>
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Disponibilité :</strong> {item.scrapedInfo.availability ? '✅ En stock' : '❌ Indisponible'}
                        </p>
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    {/* Taille */}
                    <div className="form-group">
                      <label>📏 Taille</label>
                      <input
                        type="text"
                        value={item.size}
                        onChange={(e) => handleItemChange(index, 'size', e.target.value)}
                        placeholder="Ex: M, L, XL, 42, etc."
                      />
                    </div>

                    {/* Couleur */}
                    <div className="form-group">
                      <label>🎨 Couleur</label>
                      <input
                        type="text"
                        value={item.color}
                        onChange={(e) => handleItemChange(index, 'color', e.target.value)}
                        placeholder="Ex: Rouge, Bleu, etc."
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '15px' }}>
                    {/* Quantité */}
                    <div className="form-group">
                      <label>🛍️ Quantité *</label>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                        min="1"
                        required
                      />
                    </div>

                    {/* Commentaire produit */}
                    <div className="form-group">
                      <label>💬 Commentaire (optionnel)</label>
                      <input
                        type="text"
                        value={item.comment}
                        onChange={(e) => handleItemChange(index, 'comment', e.target.value)}
                        placeholder="Notes spécifiques pour ce produit"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Commentaire général */}
            <div className="form-group">
              <label htmlFor="generalComment">💬 Commentaire général pour la commande (optionnel)</label>
              <textarea
                id="generalComment"
                value={generalComment}
                onChange={(e) => setGeneralComment(e.target.value)}
                placeholder="Instructions spéciales, notes pour tous les produits, etc."
                rows="3"
              />
            </div>

            {/* Boutons */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '30px' }}>
              <button type="submit" className="btn btn-primary" disabled={loading || Object.values(uploadingImages).some(v => v)}>
                {loading ? 'Création...' : `Créer la commande (${items.length} produit${items.length > 1 ? 's' : ''})`}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate('/')}
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default OrderForm;
